import { memo, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, ArrowRight, Check, Flame, MessageSquare, Wrench, 
  FilePlus, Zap, Calendar, Trophy, Star, Send, LogIn, Compass,
  type LucideIcon 
} from 'lucide-react';
import { useChallenges } from '@/hooks/useChallenges';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';

// Map icon string names to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  'message-square': MessageSquare,
  'wrench': Wrench,
  'file-plus': FilePlus,
  'zap': Zap,
  'calendar': Calendar,
  'trophy': Trophy,
  'star': Star,
  'send': Send,
  'log-in': LogIn,
  'compass': Compass,
  'target': Target,
};

// Map action types to routes
const ACTION_ROUTES: Record<string, string> = {
  'chat_start': '/chat',
  'message_send': '/chat',
  'tool_use': '/geniebot-everyday-suite',
  'prompt_create': '/prompt-studio',
  'login': '/dashboard',
  'unique_tools': '/tools',
};

export const DailyChallengesBar = memo(() => {
  const { dailyChallenges, weeklyChallenges, loading, claimXP } = useChallenges();
  const scrollRef = useRef<HTMLDivElement>(null);

  if (loading || (!dailyChallenges.length && !weeklyChallenges.length)) {
    return null;
  }

  const allChallenges = [...dailyChallenges, ...weeklyChallenges].slice(0, 5);

  const handleClaim = async (progressId: string) => {
    await claimXP(progressId);
    confetti({
      particleCount: 50,
      spread: 50,
      origin: { y: 0.6 },
      colors: ['#10b981', '#3b82f6', '#8b5cf6'],
    });
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Target className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Daily Genie Challenges</h3>
        <Flame className="h-4 w-4 text-orange-500 animate-pulse" />
      </div>

      <ScrollArea className="w-full pb-2" ref={scrollRef}>
        <div className="flex gap-3 pb-2">
          {allChallenges.map((cp, index) => {
            // Use a compound key to ensure uniqueness across daily/weekly challenges
            const uniqueKey = `${cp.id}-${cp.challenge?.id || index}`;
            const challenge = cp.challenge;
            const progress = cp.currentProgress;
            const target = challenge.targetCount || 1;
            const isCompleted = cp.isCompleted;
            const route = ACTION_ROUTES[challenge.actionType] || '/dashboard';
            const progressPercent = Math.min((progress / target) * 100, 100);

            return (
              <motion.div
                key={uniqueKey}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  "flex-shrink-0 min-w-[200px] max-w-[240px] p-3 rounded-xl border transition-all",
                  isCompleted 
                    ? "bg-green-500/10 border-green-500/30" 
                    : "bg-card hover:border-primary/30"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm",
                    isCompleted ? "bg-green-500 text-white" : "bg-primary/10 text-primary"
                  )}>
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      (() => {
                        const IconComponent = ICON_MAP[challenge.icon];
                        return IconComponent ? <IconComponent className="h-4 w-4" /> : <Target className="h-4 w-4" />;
                      })()
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <p className={cn(
                      "text-sm font-medium leading-tight",
                      isCompleted && "line-through text-muted-foreground"
                    )}>
                      {challenge.title}
                    </p>
                    
                    {!isCompleted && (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${progressPercent}%` }}
                              className="h-full bg-primary rounded-full"
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {progress}/{target}
                          </span>
                        </div>
                        <Link to={route}>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
                            Go <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        </Link>
                      </>
                    )}

                    {isCompleted && !cp.xpClaimed && (
                      <Button 
                        size="sm" 
                        variant="default" 
                        className="h-7 px-2 text-xs bg-green-500 hover:bg-green-600"
                        onClick={() => handleClaim(cp.id)}
                      >
                        Claim +{challenge.xpReward} XP
                      </Button>
                    )}

                    {isCompleted && cp.xpClaimed && (
                      <p className="text-xs text-green-500 font-medium">
                        ✓ +{challenge.xpReward} XP claimed
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
});

DailyChallengesBar.displayName = 'DailyChallengesBar';
